import { Dashboard, DashboardCreate } from '@src/main/modules/common/dashboard/domain/Dashboard';
import { DashboardRepository } from '@src/main/modules/common/dashboard/domain/DashboardRepository';
import { useDashboardUseCases } from '@src/main/ui/providers/dashboard/hooks/useDashboardUseCases';
import { FC, PropsWithChildren, createContext, useContext, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

interface ContextState {
  isLoading: boolean;
  dashboards?: Dashboard[];
  selectedDashboard?: Dashboard;
  setSelectedDashboard: (dashboard?: Dashboard) => void;
  addDashboard: (dashboard: DashboardCreate) => Promise<void>;
  deleteDashboard: (dashboardId: string) => void;
}

const DashboardContext = createContext<ContextState>({} as ContextState);

interface Props {
  dashboardRepository: DashboardRepository;
}

export const DashboardContextProvider: FC<PropsWithChildren<Props>> = ({
  children,
  dashboardRepository,
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const location = useLocation();
  const {
    isLoading,
    dashboards,
    selectedDashboard,
    setSelectedDashboard,
    addDashboard,
    deleteDashboard,
  } = useDashboardUseCases(dashboardRepository);

  useEffect(() => {
    if (searchParams.get('artifactId') === null) {
      setSelectedDashboard(undefined);
    }
  }, [searchParams, setSelectedDashboard]);

  const values = useMemo(
    () => ({
      isLoading,
      dashboards,
      selectedDashboard,
      setSelectedDashboard: (dashboard?: Dashboard) => {
        if (dashboard) {
          navigate(`?artifactId=${dashboard?.id}`);
        } else {
          navigate(location.pathname);
        }
        setSelectedDashboard(dashboard);
      },
      addDashboard,
      deleteDashboard,
    }),
    [
      isLoading,
      dashboards,
      selectedDashboard,
      setSelectedDashboard,
      addDashboard,
      deleteDashboard,
      navigate,
      location.pathname,
    ],
  );

  return <DashboardContext.Provider value={values}>{children}</DashboardContext.Provider>;
};

export const useDashboardContext = (): ContextState => useContext(DashboardContext);
